import { TransactionResponse } from '@ethersproject/abstract-provider'
import { BigNumber } from 'ethers'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useAccount, useContractReads, useContractWrite } from 'wagmi'
import { useAuth } from '../../../__legacy__/hooks/useAuth'
import { useMintingAPI } from '../../api/hooks/useMintingAPI'
import { ContractInformation } from '../types'
import { getContractReadInfo } from '../utils'
import { getChainId } from '../utils/getChainId'

/**
 * leaving this util here as it is
 * specific to this mint function and shouldn't be
 * generalized
 */
const getGasLimit = (): string | undefined => {
  return '300000'
}

type useUGCContractMetadataProps = ContractInformation & {
  externalUrl?: string
  maxPresalePurchasablePerWallet?: number
  preSalePrice?: number | string
  editionId?: number
}

/** This hook is used by the UGC site for minting editions on that site. */
export const useUGCContractMetadata = ({
  contractAddress,
  abi,
  chain,
  externalUrl,
  maxPresalePurchasablePerWallet,
  preSalePrice,
  editionId
}: useUGCContractMetadataProps) => {
  const { user } = useAuth()
  const { connector, address } = useAccount()
  const { getMerkleProof } = useMintingAPI()
  const { enqueueSnackbar } = useSnackbar()
  const [isMinting, setIsMinting] = useState<boolean>(false)
  const chainToUseId = getChainId(chain)
  const isExternalContract = externalUrl !== null && externalUrl !== undefined

  const contracts = getContractReadInfo({ contractAddress, abi, chain })
  const { data, isLoading: dataIsLoading } = useContractReads({
    contracts,
    staleTime: 2_000,
    enabled: !isExternalContract
  })
  const hasValidRead = !dataIsLoading && !isExternalContract
  const totalMinted: number = hasValidRead ? (data[0] ? data[0][9].toNumber() : 0) : 0
  const salePrice: string = hasValidRead ? (data[0] ? data[0][2].toString() : '0') : '0'

  const onError = (error: Error, isPresale: boolean) => {
    let message = `Error minting${isPresale ? ' presale' : ''}.`
    if (error.message.includes('insufficient funds')) {
      message += ' Insufficient funds.'
    }
    message += ' Please try again.'
    enqueueSnackbar(message, {
      variant: 'modalError'
    })
  }

  const gasLimit = getGasLimit()

  const { writeAsync: mintAsync, isLoading: isMintingLoading } = useContractWrite({
    addressOrName: contractAddress,
    contractInterface: abi,
    functionName: 'purchase',
    args: [1],
    chainId: chainToUseId,
    overrides: { gasLimit },
    onError: (error) => onError(error, false)
  })

  const { writeAsync: mintPresaleAsync, isLoading: isPresaleMintingLoading } = useContractWrite({
    addressOrName: contractAddress,
    contractInterface: abi,
    functionName: 'purchasePresale',
    chainId: chainToUseId,
    overrides: { gasLimit },
    onError: (error) => onError(error, true)
  })

  const mint = async (presale?: boolean) => {
    // if the mint comes from an external source, we just link
    // to the mint page on the external site
    if (isExternalContract) {
      window.open(externalUrl, '_blank')
      return
    }

    if (!user) return

    if (typeof window?.ethereum === 'undefined' && connector?.name === 'WalletConnect') {
      const metamaskAppDeepLink = `https://metamask.app.link/dapp/${window.location.href}`
      window.open(metamaskAppDeepLink)
      return
    }

    setIsMinting(true)
    try {
      let resp: TransactionResponse
      if (presale) {
        const merkleProof = await getMerkleProof(editionId, address)
        resp = await mintPresaleAsync({
          args: [1, maxPresalePurchasablePerWallet, BigNumber.from(preSalePrice), merkleProof],
          overrides: {
            value: BigNumber.from(preSalePrice),
            gasLimit: getGasLimit()
          }
        })
      } else {
        resp = await mintAsync({
          args: [1],
          overrides: {
            value: salePrice
          }
        })
      }
      const wait = await resp.wait()

      // mint successful
      if (wait.status === 1) {
        window.location.reload()
      }
    } catch (error) {
      console.error(error)
    }
    setIsMinting(false)
  }

  return { mint, totalMinted, isMinting: isMinting || isMintingLoading || isPresaleMintingLoading }
}
